import { FilterDefinition } from '../models/FilterDefinition/FilterDefinition';
import { RelationalOperator } from '../types/filter';
import {
  sortBooleans,
  sortMonetaryRanges,
  sortSalesStage,
} from '../utils/sort';

import {
  ControlType,
  FilterDefinitionFields,
} from '../models/FilterDefinition/FilterDefinitionFields';

export interface ReportDefinition {
  id: string;
  reportName: string;
  reportDescription?: string;
  listType: string;
  exportName: () => string;
  outputFormat?: string;
  filterDefinitions: FilterDefinition[];
  /**
   * Array of column names to use for ensuring unique rows.
   * When specified, duplicate rows based on these columns will be removed.
   */
  uniqueBy?: string[];
  /**
   * Marks a machine/vendor-bound CSV export (e.g. the Acxiom DoNotMail file that
   * is picked up verbatim by an automated upload). Such files must NOT receive a
   * UTF-8 BOM, because a leading BOM can break a naive vendor parser. Leave unset
   * (falsy) for user-facing exports so they get the BOM that makes curly quotes
   * render correctly in Excel.
   */
  skipUtf8Bom?: boolean;
}

// Shared "List Name" filter to show in Bought List reports (not exported)
const listNameFilter: FilterDefinitionFields = {
  label: 'List Name',
  controlType: ControlType.MULTI_SELECT,
  databaseMetadata: {
    columnName: 'listName',
    dataType: 'string',
  },
  csvMetadata: {
    fieldType: 'database',
    columnOrder: 0, // not included in export
    columnName: 'List Name',
  },
  defaultValue: ['ALL'],
};

const countsByGradeReportDefinitions = FilterDefinition.createFilterDefinitions(
  [
    // Exclude expired records
    {
      databaseMetadata: {
        columnName: 'expirationDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Expiration Date',
      },
      defaultValue: [new Date(Date.now())],
      defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
    },
    // Exclude optOut = 1
    {
      databaseMetadata: {
        columnName: 'optOut',
        dataType: 'boolean',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Opt Out',
      },
      defaultValue: [true],
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    // Exclude doNotMail = 1
    {
      databaseMetadata: {
        columnName: 'doNotMail',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Do Not Mail',
      },
      defaultValue: ['1'], // This is 1 to match the data currently in the database
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    // Exclude hlMatch = 1
    {
      databaseMetadata: {
        columnName: 'hlMatch',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'HL Match',
      },
      defaultValue: ['1'],
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    {
      label: 'List Name',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'listName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 1,
        columnName: 'List Name',
      },
      defaultValue: ['ALL'],
      isGroupBy: true,
    },
    {
      databaseMetadata: {
        columnName: 'site1',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 2,
        columnName: 'SITE',
      },
      isGroupBy: true,
    },
    {
      databaseMetadata: {
        columnName: 'epm3Decile1',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 3,
        columnName: 'EPM2 Grade',
      },
      isGroupBy: true,
    },
    {
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 4,
        columnName: 'List Name ID COUNT',
      },
      aggregation: {
        type: 'count',
      },
    },
  ],
  '1.0.1'
);

const doNotMailFilters = [
  // Include non-expired records and records expired within the last 180 days
  {
    databaseMetadata: {
      columnName: 'expirationDate',
      dataType: 'date',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 0,
      columnName: 'Expiration Date',
    },
    // Set lower bound to 180 days ago to include records within the 180-day post-expiration window
    defaultValue: [new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)],
    defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
  },
  // Include opted out records
  {
    databaseMetadata: {
      columnName: 'optOut',
      dataType: 'boolean',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 0,
      columnName: 'Opt Out',
    },
    defaultValue: [true],
    defaultRelationalOperator: RelationalOperator.EQUAL,
  },
  {
    databaseMetadata: {
      columnName: 'doNotMail',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 11,
      columnName: 'Do Not Mail',
    },
    defaultRelationalOperator: RelationalOperator.EQUAL,
    valueSortFn: sortBooleans,
  },
  {
    csvMetadata: {
      fieldType: 'static',
      columnOrder: 10,
      columnName: 'BL_DataService: BL Record Name',
    },
  },
  {
    databaseMetadata: {
      columnName: 'site1',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 1,
      columnName: 'Site_1',
    },
  },
  {
    databaseMetadata: {
      columnName: 'saluation', // FIXME: Typo in database column name
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 2,
      columnName: 'Salutation',
    },
  },
  {
    databaseMetadata: {
      columnName: 'firstName',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 3,
      columnName: 'First Name',
    },
  },
  {
    databaseMetadata: {
      columnName: 'lastName',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 4,
      columnName: 'Last Name',
    },
  },
  {
    databaseMetadata: {
      columnName: 'address',
      dataType: 'number',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 5,
      columnName: 'Address',
    },
  },
  {
    databaseMetadata: {
      columnName: 'city',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 6,
      columnName: 'City',
    },
  },
  {
    databaseMetadata: {
      columnName: 'state',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 7,
      columnName: 'State',
    },
  },
  {
    databaseMetadata: {
      columnName: 'zipcode',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 8,
      columnName: 'Zip Code',
    },
  },
  {
    databaseMetadata: {
      columnName: 'listName',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 12,
      columnName: 'List Name',
    },
  },
  {
    databaseMetadata: {
      columnName: 'listNameId',
      dataType: 'string',
    },
    csvMetadata: {
      fieldType: 'database',
      columnOrder: 13,
      columnName: 'List Name ID',
    },
  },
] as FilterDefinitionFields[];

const doNotMailReportDefinitions = FilterDefinition.createFilterDefinitions(
  [
    ...doNotMailFilters,
    {
      databaseMetadata: {
        columnName: 'optOutUpdatedBy',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 14,
        columnName: 'Last Updated By',
      },
    },
    listNameFilter,
    {
      label: 'Last Updated',
      controlType: ControlType.DATE_RANGE,
      databaseMetadata: {
        columnName: 'optOutDateTime',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 9,
        columnName: 'Last Updated',
      },
      defaultValue: [new Date(Date.now() - 120 * 24 * 60 * 60 * 1000)], // 120 days ago
      defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
    },
  ],
  '1.0.1'
);

// Same as doNotMailReportDefinitions but with a different column name for 'Last Updated' and without optOutUpdatedBy
const acxiomDoNotMailReportDefinitions =
  FilterDefinition.createFilterDefinitions(
    [
      ...doNotMailFilters,
      listNameFilter,
      {
        label: 'Last Updated',
        controlType: ControlType.DATE_RANGE,
        databaseMetadata: {
          columnName: 'optOutDateTime',
          dataType: 'date',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 9,
          columnName: 'BL_DataService: Last Modified Date',
        },
        defaultValue: [new Date(Date.now() - 120 * 24 * 60 * 60 * 1000)], // 120 days ago
        defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
      },
    ],
    '1.0.1'
  );

const listNameExpirationCountReportDefinitions =
  FilterDefinition.createFilterDefinitions(
    [
      // Exclude expired records
      {
        databaseMetadata: {
          columnName: 'expirationDate',
          dataType: 'date',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'Expiration Date',
        },
        defaultValue: [new Date(Date.now())],
        defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
      },
      // Exclude optOut = 1
      {
        databaseMetadata: {
          columnName: 'optOut',
          dataType: 'boolean',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'Opt Out',
        },
        defaultValue: [true],
        defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
      },
      // Exclude doNotMail = 1
      {
        databaseMetadata: {
          columnName: 'doNotMail',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'Do Not Mail',
        },
        defaultValue: ['1'], // This is 1 to match the data currently in the database
        defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
      },
      // Exclude hlMatch = 1
      {
        databaseMetadata: {
          columnName: 'hlMatch',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'HL Match',
        },
        defaultValue: ['1'],
        defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
      },
      listNameFilter,
      {
        databaseMetadata: {
          columnName: 'site1',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 1,
          columnName: 'SITE',
        },
        isGroupBy: true,
      },
      {
        databaseMetadata: {
          columnName: 'listName',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 2,
          columnName: 'List Name',
        },
        isGroupBy: true,
      },
      {
        databaseMetadata: {
          columnName: 'blSelect',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 3,
          columnName: 'BL Select',
        },
        isGroupBy: true,
      },
      {
        databaseMetadata: {
          columnName: 'listDate',
          dataType: 'date',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 4,
          columnName: 'List Date',
        },
        isGroupBy: true,
      },
      {
        databaseMetadata: {
          columnName: 'expirationDate',
          dataType: 'date',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 5,
          columnName: 'List Expiration Date',
        },
        isGroupBy: true,
      },
      {
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 6,
          columnName: 'List Name ID COUNT',
        },
        aggregation: {
          type: 'count',
        },
      },
    ],
    '1.1.0'
  );

const ccHouseListReportDefinitions = FilterDefinition.createFilterDefinitions(
  [
    {
      label: 'LOC',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'loc',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'LOC',
      },
      defaultValue: ['ALF', 'CC - ALL', 'MC', 'Respite', 'SNF'],
    },
    {
      label: 'Opportunity Site',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'opportunitySite',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 1,
        columnName: 'Opportunity Site',
      },
    },
    {
      label: 'Sales Stage',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'salesStage',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 2,
        columnName: 'Sales Stage',
      },
      valueSortFn: sortSalesStage,
      defaultValue: ['Discovery', 'Visitor'],
    },
    {
      databaseMetadata: {
        columnName: 'labelSalutation',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 3,
        columnName: 'Label Salutation',
      },
    },
    {
      databaseMetadata: {
        columnName: 'letterSalutation',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 4,
        columnName: 'Letter Salutation',
      },
    },
    {
      databaseMetadata: {
        columnName: 'firstName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 5,
        columnName: 'First Name',
      },
    },
    {
      databaseMetadata: {
        columnName: 'lastName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 6,
        columnName: 'Last Name',
      },
    },
    {
      databaseMetadata: {
        columnName: 'address',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 7,
        columnName: 'Address',
      },
    },
    {
      databaseMetadata: {
        columnName: 'city',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 8,
        columnName: 'City',
      },
    },
    {
      databaseMetadata: {
        columnName: 'state',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 9,
        columnName: 'State',
      },
    },
    {
      databaseMetadata: {
        columnName: 'zipcode',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 10,
        columnName: 'Zip Code',
      },
    },
    {
      databaseMetadata: {
        columnName: 'accountID',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 11,
        columnName: 'Account ID',
      },
    },
    {
      databaseMetadata: {
        columnName: 'contactId',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 12,
        columnName: 'Contact ID',
      },
    },
    {
      databaseMetadata: {
        columnName: 'groupName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 13,
        columnName: 'Group Name',
      },
      staffSeedMetadata: {
        staticValue: 'StaffSeed',
      },
    },
    {
      databaseMetadata: {
        columnName: 'homeValue',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 14,
        columnName: 'HoH Home Value',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      databaseMetadata: {
        columnName: 'hohIncome',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 15,
        columnName: 'HoH Income',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      databaseMetadata: {
        columnName: 'hohNetWorth',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 16,
        columnName: 'HoH Net Worth',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      databaseMetadata: {
        columnName: 'hohAge',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 17,
        columnName: 'HoH Age',
      },
    },
    {
      databaseMetadata: {
        columnName: 'distanceToSite',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 18,
        columnName: 'Distance From Site',
      },
    },
    {
      databaseMetadata: {
        columnName: 'primaryContact',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 19,
        columnName: 'Primary Contact',
      },
    },
    {
      databaseMetadata: {
        columnName: 'contactType',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 20,
        columnName: 'Contact Type',
      },
    },
    {
      databaseMetadata: {
        columnName: 'salesOptionDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 21,
        columnName: 'Opportunity Created Date',
      },
    },
  ],
  '1.1.0'
);

const depositorPurgeReportDefinitions =
  FilterDefinition.createFilterDefinitions(
    [
      {
        label: 'LOC',
        controlType: ControlType.MULTI_SELECT,
        databaseMetadata: {
          columnName: 'loc',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'LOC',
        },
      },
      {
        label: 'Sites',
        controlType: ControlType.MULTI_SELECT,
        databaseMetadata: {
          columnName: 'primarySite',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 0,
          columnName: 'Sites',
        },
        staffSeedMetadata: {
          dbColumnName: 'community',
        },
      },
      {
        label: 'Sales Stage',
        controlType: ControlType.MULTI_SELECT,
        databaseMetadata: {
          columnName: 'salesStage',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 3,
          columnName: 'SALES_STAGE',
        },
        staffSeedMetadata: {
          staticValue: 'Staff Seed',
        },
        defaultValue: ['Reserved', 'Settled', 'Resident'],
        valueSortFn: sortSalesStage,
      },
      {
        databaseMetadata: {
          columnName: 'groupName',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 2,
          columnName: 'ACCOUNT_NAME',
        },
      },
      {
        databaseMetadata: {
          columnName: 'accountID',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 1,
          columnName: 'Account ID',
        },
      },
    ],
    '1.0.1'
  );

export const staffSeedReportDefinitions =
  FilterDefinition.createFilterDefinitions(
    [
      {
        databaseMetadata: {
          columnName: 'community',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 1,
          columnName: 'Site',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 2,
          columnName: 'ListName',
        },
        defaultValue: ['StaffSeed'],
        // staticValue: 'StaffSeed',
      },
      {
        databaseMetadata: {
          columnName: 'labelSalutation',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 3,
          columnName: 'LabelSalutation',
        },
      },
      {
        databaseMetadata: {
          columnName: 'letterSalutation',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 4,
          columnName: 'LetterSalutation',
        },
      },
      {
        databaseMetadata: {
          columnName: 'firstName',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 5,
          columnName: 'FirstName',
        },
      },
      {
        databaseMetadata: {
          columnName: 'lastName',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 6,
          columnName: 'LastName',
        },
      },
      {
        databaseMetadata: {
          columnName: 'address',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 7,
          columnName: 'Address',
        },
      },
      {
        databaseMetadata: {
          columnName: 'city',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 8,
          columnName: 'City',
        },
      },
      {
        databaseMetadata: {
          columnName: 'state',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 9,
          columnName: 'State',
        },
      },
      {
        databaseMetadata: {
          columnName: 'postalCode',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 10,
          columnName: 'ZipCode',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 11,
          columnName: 'BatchCode',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 12,
          columnName: 'ListName',
        },
        // staticValue: 'StaffSeed',
        defaultValue: ['StaffSeed'],
      },
      {
        databaseMetadata: {
          columnName: 'seedDataName',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 13,
          columnName: 'ListName ID',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 14,
          columnName: 'qseq',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 15,
          columnName: 'BL Select',
        },
        // staticValue: 'StaffSeed',
        defaultValue: ['StaffSeed'],
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 16,
          columnName: 'EPM2 Decile',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 17,
          columnName: 'EPM2 Score',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 18,
          columnName: 'HoH Home Value',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 19,
          columnName: 'Household Income',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 20,
          columnName: 'County',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 21,
          columnName: 'List Date',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 22,
          columnName: 'Net New',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 23,
          columnName: 'Gender',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 24,
          columnName: 'DropDate',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 25,
          columnName: 'Distance To Site',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 26,
          columnName: 'HoH Age',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 27,
          columnName: 'Gender',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 28,
          columnName: 'Zip5',
        },
      },
      {
        label: 'LOC',
        controlType: ControlType.MULTI_SELECT,
        databaseMetadata: {
          columnName: 'loc',
          dataType: 'string',
        },
        csvMetadata: {
          fieldType: 'database',
          columnOrder: 29,
          columnName: 'LOC',
        },
      },
      {
        csvMetadata: {
          fieldType: 'static',
          columnOrder: 30,
          columnName: 'Random Number',
        },
      },
    ],
    '1.0.0'
  );

export const reportDefinitions: ReportDefinition[] = [
  {
    id: 'report-bl-counts-by-grade',
    reportName: 'Bought List - Counts By Grade',
    reportDescription: 'Used for Planning Decks',
    listType: 'boughtlist',
    outputFormat: 'xlsx',
    exportName: () => {
      return `CountsByGrade_${new Date().toISOString().split('T')[0]}`;
    },
    filterDefinitions: countsByGradeReportDefinitions,
  },
  {
    id: 'report-bl-do-not-mail',
    reportName: 'Bought List - Do Not Mail Purge File',
    reportDescription: 'Purge BL DNM flagged records from multi-drop projects',
    listType: 'boughtlist',
    outputFormat: 'xlsx',
    exportName: () => {
      return `DoNotMail_${new Date().toISOString().split('T')[0]}`;
    },
    filterDefinitions: doNotMailReportDefinitions,
  },
  {
    id: 'report-bl-acxiom-do-not-mail',
    reportName: 'Acxiom Report - Bought List - Do Not Mail Report',
    reportDescription:
      'DO NOT USE: source of weekly automated process w Acxiom',
    listType: 'boughtlist',
    outputFormat: 'csv',
    // Vendor/machine-bound CSV consumed by the automated Acxiom upload: must not
    // receive a UTF-8 BOM (see combineCsvFiles / createUploadStream).
    skipUtf8Bom: true,
    exportName: () => {
      return `DoNotMail_${new Date().toISOString().split('T')[0]}`;
    },
    filterDefinitions: acxiomDoNotMailReportDefinitions,
  },
  {
    id: 'report-bl-list-name-expiration-count',
    reportName: 'Bought List - List Names with Expiration Dates and Counts',
    reportDescription: 'Monitor and time prospect list purchases',
    listType: 'boughtlist',
    outputFormat: 'xlsx',
    exportName: () => {
      return `BLListNameswExpiration`;
    },
    filterDefinitions: listNameExpirationCountReportDefinitions,
  },
  {
    id: 'report-hl-cc-house-list',
    reportName: 'CC House List: Non Depositors',
    reportDescription:
      'CC ad hoc DM: all sites, all CC LOC, Discovery & Visitor only',
    listType: 'houselist',
    outputFormat: 'xlsx',
    exportName: () => {
      return 'CC_ESL_HLDiscoveryVisitor_DM';
    },
    filterDefinitions: ccHouseListReportDefinitions,
  },
  {
    id: 'report-hl-depositor-purge',
    reportName: 'Depositor Purge File',
    reportDescription: 'To remove Reserved, Settled, Residents on Account ID',
    listType: 'houselist',
    outputFormat: 'xlsx',
    exportName: () => {
      return `Depositors_PurgeFile${new Date().toISOString().split('T')[0]}`;
    },
    filterDefinitions: depositorPurgeReportDefinitions,
    uniqueBy: ['Account ID'], // Ensure unique rows based on Account ID
  },
  {
    id: 'report-staff-seed',
    reportName: 'Staff Seed',
    reportDescription:
      'CC or IL Staff Seed any or all sites, matches HL Export',
    listType: 'staffseed',
    outputFormat: 'xlsx',
    exportName: () => {
      return `StaffSeed_${new Date().toISOString().split('T')[0]}`;
    },
    filterDefinitions: staffSeedReportDefinitions,
  },
];
